import SurveyButton from '@/components/Base/SurveyButton';
import SurveyBackgroundLayout from '@/components/layout/SurveyBackgroundLayout';
import {
  SurveyContentPanelInset,
  SurveyFormPanel,
  surveyQuestionTextClass,
} from '@/components/layout/SurveyContentPanel';
import SurveyValidationAlert from '@/components/survey/SurveyValidationAlert';
import { buildLdSurveySubmitBodyFromStorage, submitCustomerSurveyTng } from '@/api/customerSurvey';
import { useSurveyLanguage } from '@/hooks/useSurveyLanguage';
import { useTranslation } from '@/hooks/useTranslation';
import { clearAllLocalStorage } from '@/lib/clearAllLocalStorage';
import { isPrevBrandAnswerComplete, readPrevBrandAnswerAnswers } from '@/lib/prevBrandAnswerStorage';
import { isPrevBrandComplete } from '@/lib/prevBrandStorage';
import { surveyProfileRedirect, surveyQuestionnaireRedirect } from '@/lib/surveyFlow';
import { writeTngRewardSession } from '@/lib/tngRewardSession';
import { readWhyNotLdAnswers, writeWhyNotLdAnswers } from '@/lib/whyNotLdStorage';
import { useAppSelector } from '@/stores/hooks';
import { selectOtpDraft, selectPdpaConsent, selectSurveyScreening } from '@/stores/selectors';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

const textareaClass =
  'min-h-[140px] min-w-0 w-full resize-y rounded-sm border border-slate-800 bg-white px-2 py-2 text-[15px] leading-tight text-slate-900 outline-none placeholder:text-slate-700 focus:border-slate-600 sm:min-h-[160px] sm:text-[16px]';

export default function WhyNotLd() {
  const navigate = useNavigate();
  const { surveyLanguage } = useSurveyLanguage();
  const { t } = useTranslation();
  const surveyScreening = useAppSelector(selectSurveyScreening);
  const pdpaConsent = useAppSelector(selectPdpaConsent);
  const { customerUuid, otpVerified, questionnaireSet, mobileLocalDigits } = useAppSelector(selectOtpDraft);

  const [answer, setAnswer] = useState(() => readWhyNotLdAnswers().answer);
  const [warning, setWarning] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    writeWhyNotLdAnswers({ answer });
  }, [answer]);

  if (surveyLanguage == null) return <Navigate to="/" replace />;
  if (surveyScreening == null) return <Navigate to="/age_smoker" replace />;
  if (!pdpaConsent) return <Navigate to="/pdpa" replace />;
  if (!customerUuid || !otpVerified) return <Navigate to="/phone" replace />;
  const questionnaireRedirect = surveyQuestionnaireRedirect(questionnaireSet);
  if (questionnaireRedirect) return <Navigate to={questionnaireRedirect} replace />;
  const profileRedirect = surveyProfileRedirect(questionnaireSet);
  if (profileRedirect) return <Navigate to={profileRedirect} replace />;
  if (questionnaireSet === 'B') return <Navigate to="/ldproductnow" replace />;
  if (!isPrevBrandComplete()) return <Navigate to="/survey/prevbrand" replace />;
  if (!isPrevBrandAnswerComplete()) return <Navigate to="/survey/prevbrandanswer" replace />;

  const smokedOtherProduct = readPrevBrandAnswerAnswers().smokedOtherProduct;
  if (smokedOtherProduct === 'yes') {
    return <Navigate to="/survey/prevbrandanswer-other" replace />;
  } else if (smokedOtherProduct !== 'no') {
    return <Navigate to="/survey/prevbrandanswer" replace />;
  }

  const handleSubmit = () => {
    const trimmed = answer.trim();
    if (!trimmed) {
      setWarning(t.whyNotLd.answerWarning);
      return;
    }
    if (!customerUuid) {
      setWarning(t.whyNotLd.sessionExpiredWarning);
      return;
    }

    writeWhyNotLdAnswers({ answer: trimmed });

    const body = buildLdSurveySubmitBodyFromStorage(mobileLocalDigits);
    if (!body) {
      setWarning(t.whyNotLd.incompleteWarning);
      return;
    }

    setSubmitting(true);
    setWarning(null);
    void submitCustomerSurveyTng(customerUuid, body)
      .then((result) => {
        writeTngRewardSession({
          pin: result.pin,
          expirydate: result.expirydate,
          value: result.value,
        });
        clearAllLocalStorage();
        navigate('/tng', { replace: true });
      })
      .catch((err: unknown) => {
        setWarning(err instanceof Error ? err.message : t.whyNotLd.submitError);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <SurveyBackgroundLayout>
      <div className="relative z-10 flex h-[100dvh] max-h-[100dvh] flex-col pb-4">
        <div className="mx-auto flex min-h-0 w-full max-w-full flex-1 flex-col pt-[9dvh]">
          <SurveyContentPanelInset className="min-h-0 flex-1 overflow-y-auto">
            <SurveyFormPanel className="flex flex-col gap-2">
              <p className={surveyQuestionTextClass}>{t.whyNotLd.question}</p>
              <textarea
                id="why-not-ld-answer"
                value={answer}
                onChange={(e) => {
                  setAnswer(e.target.value);
                  setWarning(null);
                }}
                placeholder={t.whyNotLd.placeholder}
                className={textareaClass}
              />
            </SurveyFormPanel>
          </SurveyContentPanelInset>

          <SurveyContentPanelInset className="mt-4 shrink-0">
            <div className="flex flex-col items-center gap-3">
              {warning != null ? <SurveyValidationAlert>{warning}</SurveyValidationAlert> : null}
              <SurveyButton className="w-full" disabled={submitting} onClick={handleSubmit}>
                {t.whyNotLd.submit}
              </SurveyButton>
            </div>
          </SurveyContentPanelInset>
        </div>
      </div>
    </SurveyBackgroundLayout>
  );
}
